import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  STORE_JOB_ROLES,
  STORE_STAFF_JOB_ROLES_BY_STORE,
  TIP_POOLS,
  TIP_POOL_RULES,
  CREATE_STORE_JOB_ROLE,
  UPDATE_STORE_JOB_ROLE,
  DELETE_STORE_JOB_ROLE,
} from "src/constants/queries";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import type { StoreJobRole, StoreJobRoleInput, StoreStaffJobRole } from "../types/payroll.types";

interface JobRolesTabProps {
  onUpdate?: () => void;
  onNavigateToTipPools?: () => void;
  onNavigateToEmployeeRoles?: () => void;
}

export function JobRolesTab({ onUpdate, onNavigateToTipPools, onNavigateToEmployeeRoles }: JobRolesTabProps) {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const [roles, setRoles] = useState<StoreJobRole[]>([]);
  const [assignments, setAssignments] = useState<StoreStaffJobRole[]>([]);
  const [poolCountByRoleId, setPoolCountByRoleId] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StoreJobRole | null>(null);
  const [form, setForm] = useState<{ name: string }>({ name: "" });
  const [deleteBlocked, setDeleteBlocked] = useState<{ empCount: number; poolCount: number } | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axBe.post<{ data?: { storeJobRoles?: StoreJobRole[] } }>(GRAPHQL_ENDPOINT, {
        query: STORE_JOB_ROLES,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: false },
      });
      setRoles(data?.data?.storeJobRoles ?? []);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to load job roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchAssignments = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data } = await axBe.post<{ data?: { storeStaffJobRolesByStore?: StoreStaffJobRole[] } }>(
        GRAPHQL_ENDPOINT,
        {
          query: STORE_STAFF_JOB_ROLES_BY_STORE,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        }
      );
      setAssignments(data?.data?.storeStaffJobRolesByStore ?? []);
    } catch {
      setAssignments([]);
    }
  }, [hasRestaurant?.correlationId, axBe]);

  const fetchPoolUsageByRole = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    try {
      const { data: poolsData } = await axBe.post<{ data?: { tipPools?: { id: number }[] } }>(GRAPHQL_ENDPOINT, {
        query: TIP_POOLS,
        variables: { storeCorrelationId: hasRestaurant.correlationId, activeOnly: false },
      });
      const pools = poolsData?.data?.tipPools ?? [];
      const allRules = await Promise.all(
        pools.map((pool) =>
          axBe
            .post<{ data?: { tipPoolRules?: { storeJobRoleId: number }[] } }>(GRAPHQL_ENDPOINT, {
              query: TIP_POOL_RULES,
              variables: { tipPoolId: pool.id },
            })
            .then((res) => (res.data?.data?.tipPoolRules ?? []).map((r) => ({ tipPoolId: pool.id, storeJobRoleId: r.storeJobRoleId })))
        )
      );
      const flat = allRules.flat();
      const poolIdsByRoleId = flat.reduce<Record<number, Set<number>>>((acc, r) => {
        if (!acc[r.storeJobRoleId]) acc[r.storeJobRoleId] = new Set();
        acc[r.storeJobRoleId].add(r.tipPoolId);
        return acc;
      }, {});
      const count: Record<number, number> = {};
      Object.entries(poolIdsByRoleId).forEach(([roleId, set]) => {
        count[Number(roleId)] = set.size;
      });
      setPoolCountByRoleId(count);
    } catch {
      setPoolCountByRoleId({});
    }
  }, [hasRestaurant?.correlationId, axBe]);

  useEffect(() => {
    fetchRoles();
    fetchAssignments();
    fetchPoolUsageByRole();
  }, [fetchRoles, fetchAssignments, fetchPoolUsageByRole]);

  const assignmentCountByRoleId = assignments.reduce<Record<number, number>>((acc, a) => {
    acc[a.storeJobRoleId] = (acc[a.storeJobRoleId] ?? 0) + 1;
    return acc;
  }, {});

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "" });
    setModalOpen(true);
  };
  const openEdit = (role: StoreJobRole) => {
    setEditing(role);
    setForm({ name: role.name });
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!hasRestaurant?.correlationId || !form.name.trim()) return;
    const input: StoreJobRoleInput = { name: form.name.trim(), sortOrder: 0 };
    try {
      if (editing) {
        await axBe.post(GRAPHQL_ENDPOINT, {
          query: UPDATE_STORE_JOB_ROLE,
          variables: { id: editing.id, input },
        });
      } else {
        await axBe.post(GRAPHQL_ENDPOINT, {
          query: CREATE_STORE_JOB_ROLE,
          variables: { storeCorrelationId: hasRestaurant.correlationId, input },
        });
      }
      closeModal();
      fetchRoles();
      onUpdate?.();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to save");
    }
  };

  const handleDelete = async (roleId: number) => {
    const empCount = assignmentCountByRoleId[roleId] ?? 0;
    const poolCount = poolCountByRoleId[roleId] ?? 0;
    if (empCount > 0 || poolCount > 0) {
      setDeleteBlocked({ empCount, poolCount });
      return;
    }
    if (!window.confirm("Delete this job role?")) return;
    try {
      await axBe.post(GRAPHQL_ENDPOINT, {
        query: DELETE_STORE_JOB_ROLE,
        variables: { id: roleId },
      });
      fetchRoles();
      fetchAssignments();
      fetchPoolUsageByRole();
      onUpdate?.();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? "Failed to delete");
    }
  };

  return (
    <div>
      <p className="text-sm text-[#6b7280] mb-6">
        These roles determine clock-in options and tip distribution rules.
      </p>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {!loading && roles.length > 0 && (
        <div className="flex justify-end mb-4">
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95"
          >
            <span aria-hidden>+</span>
            Add Job Role
          </button>
        </div>
      )}
      {loading ? (
        <p className="text-sm text-[#6b7280]">Loading…</p>
      ) : roles.length === 0 ? (
        <div className="rounded-xl border border-[#e5e7eb] bg-[#fafafa] py-12 px-6 text-center">
          <p className="text-[#374151] font-medium mb-1">No job roles created yet.</p>
          <p className="text-sm text-[#6b7280] mb-6">
            Create job roles to assign employees and configure tip pools.
          </p>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95"
          >
            <span aria-hidden>+</span>
            Create first role
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-[#e5e7eb]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left border-collapse">
            <thead>
              <tr className="bg-[#f9fafb] border-b border-[#e5e7eb]">
                <th className="py-3 px-4 text-xs font-semibold uppercase text-[#6b7280]">Name</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase text-[#6b7280]">Used by</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase text-[#6b7280]">In pools</th>
                <th className="py-3 px-4 text-xs font-semibold uppercase text-[#6b7280] whitespace-nowrap w-32 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const empCount = assignmentCountByRoleId[role.id] ?? 0;
                const pools = poolCountByRoleId[role.id] ?? 0;
                return (
                  <tr key={role.id} className="border-t border-[#e5e7eb] hover:bg-[#f9fafb]">
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#111827]">{role.name}</span>
                        {role.active !== false && (
                          <span className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-4 text-sm text-[#6b7280]">
                      {empCount > 0 ? `${empCount} employee${empCount === 1 ? "" : "s"}` : "—"}
                    </td>
                    <td className="py-2 px-4 text-sm text-[#6b7280]">
                      {pools > 0 ? (
                        <button
                          type="button"
                          onClick={() => onNavigateToTipPools?.()}
                          className="text-[#FD7657] font-medium hover:underline inline-flex items-center gap-1"
                        >
                          {pools} pool{pools === 1 ? "" : "s"}
                          <span aria-hidden>→</span>
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 px-4 whitespace-nowrap text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(role)}
                          className="text-[#FD7657] text-sm font-medium hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(role.id)}
                          className="text-red-600 text-sm font-medium hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {deleteBlocked && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl border border-[#E5E7EB] w-full max-w-md p-7 shadow-[0_20px_40px_rgba(0,0,0,0.12)]">
            <div className="flex items-center gap-3 mb-7">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600" aria-hidden>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </span>
              <h3 className="text-lg font-semibold text-[#111827] leading-tight">Cannot delete this role</h3>
            </div>
            <p className="text-sm text-[#9ca3af] mb-2">This role is currently:</p>
            <ul className="text-sm text-[#6b7280] list-disc list-inside space-y-1 mb-4 ml-1">
              {deleteBlocked.empCount > 0 && (
                <li>Assigned to {deleteBlocked.empCount} employee{deleteBlocked.empCount === 1 ? "" : "s"}</li>
              )}
              {deleteBlocked.poolCount > 0 && (
                <li>Used in {deleteBlocked.poolCount} tip pool{deleteBlocked.poolCount === 1 ? "" : "s"}</li>
              )}
            </ul>
            <p className="text-sm text-[#9ca3af] mb-6">Remove these references before deleting.</p>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setDeleteBlocked(null)}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              {deleteBlocked.poolCount > 0 && onNavigateToTipPools && (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteBlocked(null);
                    onNavigateToTipPools();
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    deleteBlocked.empCount > 0 && onNavigateToEmployeeRoles
                      ? "border border-[#e5e7eb] text-[#374151] hover:bg-[#f9fafb]"
                      : "bg-[#FD7657] text-white hover:opacity-95"
                  }`}
                >
                  View tip pools
                </button>
              )}
              {deleteBlocked.empCount > 0 && onNavigateToEmployeeRoles && (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteBlocked(null);
                    onNavigateToEmployeeRoles();
                  }}
                  className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95"
                >
                  View assignments
                </button>
              )}
              {!((deleteBlocked.empCount > 0 && onNavigateToEmployeeRoles) || (deleteBlocked.poolCount > 0 && onNavigateToTipPools)) && (
                <button
                  type="button"
                  onClick={() => setDeleteBlocked(null)}
                  className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95"
                >
                  Understood
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-[#111827] mb-4">{editing ? "Edit job role" : "Add job role"}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-[#e5e7eb] rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g. Server"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 rounded-lg border border-[#e5e7eb] text-sm font-medium text-[#374151] hover:bg-[#f9fafb]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!form.name.trim()}
                className="px-4 py-2 rounded-lg bg-[#FD7657] text-white text-sm font-medium hover:opacity-95 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
